import React, { useState, useMemo, useEffect } from 'react';
import { BrokerOrder } from '../store/brokerStore';
import { getInstrumentSpec } from '../config/instruments';
import './SplitOrderModal.css';

export interface SplitOrderModalProps {
  order: BrokerOrder;
  isLongPosition: boolean; // Position direction for determining tick offset
  onSplit: (order: BrokerOrder, qty1: number, qty2: number, price1: number, price2: number) => void;
  onClose: () => void;
}

type SplitMode = 'even' | 'custom';

const SplitOrderModal: React.FC<SplitOrderModalProps> = ({
  order,
  isLongPosition,
  onSplit,
  onClose,
}) => {
  const orderPrice = order.stopPrice || order.price || 0;
  const orderQty = order.orderQty || 1;
  const isStopOrder = order.ordType?.toLowerCase().includes('stop') ?? false;
  const orderTypeLabel = isStopOrder ? 'Stop Loss' : 'Take Profit';

  // Get tick size for the symbol - use order's symbol directly
  const tickSize = useMemo(() => {
    if (!order.symbol) return 0.25; // Default tick size
    const spec = getInstrumentSpec(order.symbol);
    return spec.tickSize;
  }, [order.symbol]);

  // Split mode
  const [mode, setMode] = useState<SplitMode>('even');

  // Even split values
  const evenQty1 = Math.floor(orderQty / 2);
  const evenQty2 = orderQty - evenQty1;

  // Custom split values
  const [customQty1, setCustomQty1] = useState<string>(evenQty1.toString());
  const [customQty2, setCustomQty2] = useState<string>(evenQty2.toString());

  // Reset custom values when switching to custom mode
  useEffect(() => {
    if (mode === 'custom') {
      setCustomQty1(evenQty1.toString());
      setCustomQty2(evenQty2.toString());
    }
  }, [mode, evenQty1, evenQty2]);

  // Parse and validate custom quantities
  const parsedQty1 = parseInt(customQty1, 10) || 0;
  const parsedQty2 = parseInt(customQty2, 10) || 0;

  // Get active quantities based on mode
  const activeQty1 = mode === 'even' ? evenQty1 : parsedQty1;
  const activeQty2 = mode === 'even' ? evenQty2 : parsedQty2;

  // Validation
  const validationErrors: string[] = [];

  if (activeQty1 < 1) {
    validationErrors.push('First order must have at least 1 contract');
  }
  if (activeQty2 < 1) {
    validationErrors.push('Second order must have at least 1 contract');
  }
  if (activeQty1 + activeQty2 !== orderQty) {
    validationErrors.push(`Total must equal ${orderQty} (currently ${activeQty1 + activeQty2})`);
  }
  if (mode === 'custom' && (isNaN(parsedQty1) || isNaN(parsedQty2))) {
    validationErrors.push('Please enter valid numbers');
  }

  const isValid = validationErrors.length === 0;

  // Calculate prices for the split orders
  // One order stays at original price, second is 1 tick offset for visual distinction
  // Direction of offset depends on order type and position direction
  const calculateSplitPrices = (): { price1: number; price2: number } => {
    // Price1 always at original price (the "primary" order)
    const price1 = orderPrice;

    // Price2 offset direction:
    // For TP orders: offset away from entry (more conservative take profit)
    // For SL orders: offset away from entry (wider stop, more room)
    let price2: number;

    if (isStopOrder) {
      // SL order
      if (isLongPosition) {
        // Long position SL is a Sell Stop below entry
        // Offset further down (more room to breathe)
        price2 = orderPrice - tickSize;
      } else {
        // Short position SL is a Buy Stop above entry
        // Offset further up (more room to breathe)
        price2 = orderPrice + tickSize;
      }
    } else {
      // TP order (limit)
      if (isLongPosition) {
        // Long position TP is a Sell Limit above entry
        // Offset further up (more profit on second portion)
        price2 = orderPrice + tickSize;
      } else {
        // Short position TP is a Buy Limit below entry
        // Offset further down (more profit on second portion)
        price2 = orderPrice - tickSize;
      }
    }

    return { price1, price2 };
  };

  const { price1, price2 } = calculateSplitPrices();

  const handleSplit = () => {
    if (!isValid) return;
    onSplit(order, activeQty1, activeQty2, price1, price2);
    onClose();
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid) {
      handleSplit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="split-order-backdrop" onClick={onClose}>
      <div
        className="split-order-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="split-order-header">
          <h3>Split {orderTypeLabel} Order</h3>
          <button className="split-order-close" onClick={onClose}>×</button>
        </div>

        <div className="split-order-info">
          <div className="split-order-info-row">
            <span className="info-label">Type</span>
            <span className={`info-value ${isStopOrder ? 'stop' : 'limit'}`}>
              {isStopOrder ? 'Stop' : 'Limit'}
            </span>
          </div>
          <div className="split-order-info-row">
            <span className="info-label">Price</span>
            <span className="info-value price">{orderPrice.toFixed(2)}</span>
          </div>
          <div className="split-order-info-row">
            <span className="info-label">Quantity</span>
            <span className="info-value qty">{orderQty}</span>
          </div>
        </div>

        <div className="split-order-modes">
          <button
            className={`split-mode-btn ${mode === 'even' ? 'active' : ''}`}
            onClick={() => setMode('even')}
          >
            <span className="mode-icon">½</span>
            <span className="mode-label">50/50 Split</span>
          </button>
          <button
            className={`split-mode-btn ${mode === 'custom' ? 'active' : ''}`}
            onClick={() => setMode('custom')}
          >
            <span className="mode-icon">✎</span>
            <span className="mode-label">Custom</span>
          </button>
        </div>

        <div className="split-order-preview">
          <div className="split-preview-header">Split Preview</div>

          {mode === 'even' ? (
            <div className="split-preview-rows">
              <div className="split-preview-row">
                <span className="preview-label">Order 1</span>
                <span className="preview-qty">{evenQty1} contract{evenQty1 !== 1 ? 's' : ''}</span>
                <span className="preview-price">@ {price1.toFixed(2)}</span>
              </div>
              <div className="split-preview-row">
                <span className="preview-label">Order 2</span>
                <span className="preview-qty">{evenQty2} contract{evenQty2 !== 1 ? 's' : ''}</span>
                <span className="preview-price offset">@ {price2.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="split-custom-inputs">
              <div className="split-input-row">
                <label className="split-input-label">Order 1</label>
                <input
                  type="number"
                  className="split-input"
                  value={customQty1}
                  onChange={(e) => setCustomQty1(e.target.value)}
                  min={1}
                  max={orderQty - 1}
                  autoFocus
                />
                <span className="split-input-price">@ {price1.toFixed(2)}</span>
              </div>
              <div className="split-input-row">
                <label className="split-input-label">Order 2</label>
                <input
                  type="number"
                  className="split-input"
                  value={customQty2}
                  onChange={(e) => setCustomQty2(e.target.value)}
                  min={1}
                  max={orderQty - 1}
                />
                <span className="split-input-price offset">@ {price2.toFixed(2)}</span>
              </div>
              <div className="split-total-row">
                <span className="total-label">Total:</span>
                <span className={`total-value ${activeQty1 + activeQty2 === orderQty ? 'valid' : 'invalid'}`}>
                  {activeQty1 + activeQty2} / {orderQty}
                </span>
              </div>
            </div>
          )}

          <div className="split-tick-info">
            <span className="tick-info-icon">ℹ</span>
            <span className="tick-info-text">
              Second order offset by 1 tick ({tickSize.toFixed(2)}) for visual distinction
            </span>
          </div>
        </div>

        {validationErrors.length > 0 && (
          <div className="split-order-errors">
            {validationErrors.map((error, i) => (
              <div key={i} className="split-error">{error}</div>
            ))}
          </div>
        )}

        <div className="split-order-actions">
          <button className="split-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="split-confirm-btn"
            onClick={handleSplit}
            disabled={!isValid}
          >
            Split Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitOrderModal;
